import { Package, TrendingUp, TrendingDown, AlertTriangle, Users, Percent } from 'lucide-react';

interface ReportKPIsProps {
  reportType: string;
  data: any;
}

export default function ReportKPIs({ reportType, data }: ReportKPIsProps) {
  const getKPIs = () => {
    const summary = data.summary || {};
    const kpis = summary.kpis || {};

    switch (reportType) {
      case 'inventory':
        return [
          { label: 'Total Pallets', value: (kpis.totalPallets || 0).toLocaleString(), icon: Package, color: 'blue' },
          { label: 'Good Quantity', value: (kpis.totalGoodQty || 0).toLocaleString(), icon: TrendingUp, color: 'green' },
          { label: 'Damaged Quantity', value: (kpis.totalDamagedQty || 0).toLocaleString(), icon: AlertTriangle, color: 'red' },
          { label: 'Active Clients', value: (kpis.uniqueClients || 0).toString(), icon: Users, color: 'purple' },
        ];

      case 'inward':
        return [
          { label: 'Total Records', value: (kpis.totalRecords || 0).toLocaleString(), icon: Package, color: 'blue' },
          { label: 'Total Pallets', value: (kpis.totalPallets || 0).toLocaleString(), icon: TrendingUp, color: 'green' },
          { label: 'Good Quantity', value: (kpis.totalGoodQty || 0).toLocaleString(), icon: TrendingUp, color: 'green' },
          { label: 'Damaged Quantity', value: (kpis.totalDamagedQty || 0).toLocaleString(), icon: AlertTriangle, color: 'orange' },
        ];

      case 'outward':
        return [
          { label: 'Total Records', value: (kpis.totalRecords || 0).toLocaleString(), icon: Package, color: 'blue' },
          { label: 'Total Pallets', value: (kpis.totalPallets || 0).toLocaleString(), icon: TrendingDown, color: 'red' },
          { label: 'Good Quantity', value: (kpis.totalGoodQty || 0).toLocaleString(), icon: TrendingDown, color: 'red' },
        ];

      case 'damage':
        return [
          { label: 'Affected Products', value: (kpis.affectedProducts || 0).toLocaleString(), icon: Package, color: 'red' },
          { label: 'Total Damaged', value: (kpis.totalDamaged || 0).toLocaleString(), icon: AlertTriangle, color: 'red' },
          { label: 'DAM Quantity', value: (kpis.totalDamQty || 0).toLocaleString(), icon: AlertTriangle, color: 'red' },
          { label: 'CDAM Quantity', value: (kpis.totalCdamQty || 0).toLocaleString(), icon: AlertTriangle, color: 'orange' },
        ];

      case 'client-utilization':
        const avgUtil = kpis.avgUtilization || 0;
        return [
          { label: 'Total Clients', value: (kpis.totalClients || 0).toLocaleString(), icon: Users, color: 'blue' },
          { label: 'Avg Utilization', value: `${avgUtil.toFixed(1)}%`, icon: Percent, color: avgUtil >= 80 ? 'red' : 'green' },
          { label: 'Total Allocated', value: (kpis.totalAllocated || 0).toLocaleString(), icon: Package, color: 'blue' },
          { label: 'Total Used', value: (kpis.totalUsed || 0).toLocaleString(), icon: TrendingUp, color: 'green' },
        ];

      case 'additional-space':
        return [
          { label: 'Total Records', value: (summary.totalRecords || 0).toLocaleString(), icon: Package, color: 'blue' },
          { label: 'Active Records', value: (summary.activeRecords || 0).toLocaleString(), icon: TrendingUp, color: 'green' },
          { label: 'Total Space (Sqft)', value: (summary.totalAdditionalSpace || 0).toLocaleString(), icon: Package, color: 'orange' },
          { label: 'Unique Clients', value: (summary.uniqueClients || 0).toLocaleString(), icon: Users, color: 'purple' },
        ];

      default:
        return [];
    }
  };

  const kpis = getKPIs();

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-brand-100 text-brand-600',
      green: 'bg-green-100 text-green-600',
      red: 'bg-red-100 text-red-600',
      orange: 'bg-orange-100 text-orange-600',
      purple: 'bg-purple-100 text-purple-600',
      yellow: 'bg-yellow-100 text-yellow-600',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      {kpis.map((kpi, index) => (
        <div key={index} className="bg-white rounded-xl p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">{kpi.label}</p>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            </div>
            <div className={`p-3 rounded-lg ${getColorClasses(kpi.color)}`}>
              <kpi.icon className="h-6 w-6" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
